/**
 * Admin API Service for Kartal Air
 */

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Get stored auth token
 */
const getToken = () => localStorage.getItem('admin_token');

/**
 * Generic fetch wrapper with auth
 */
async function fetchAdminAPI(endpoint, options = {}) {
  const url = `${API_BASE_URL}/api${endpoint}`;
  const token = getToken();
  
  const defaultHeaders = {
    'Content-Type': 'application/json',
  };

  if (token) {
    defaultHeaders['Authorization'] = `Bearer ${token}`;
  }

  const config = {
    ...options,
    headers: {
      ...defaultHeaders,
      ...options.headers,
    },
  };

  const response = await fetch(url, config);
  
  if (response.status === 401) {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    window.location.href = '/admin/login';
    throw new Error('Unauthorized');
  }
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
  }
  
  if (response.status === 204) {
    return null;
  }
  
  return await response.json();
}

// ============ Auth API ============
export const authAPI = {
  login: async (email, password) => {
    const response = await fetch(`${API_BASE_URL}/api/admin/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'Giriş başarısız');
    }
    
    const data = await response.json();
    localStorage.setItem('admin_token', data.access_token);
    localStorage.setItem('admin_user', JSON.stringify(data.admin));
    return data;
  },
  
  logout: () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
  },
  
  getMe: () => fetchAdminAPI('/admin/me'),
  
  updateProfile: async (data) => {
    const result = await fetchAdminAPI('/admin/me/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    // Update stored user and token
    if (result.admin) {
      localStorage.setItem('admin_user', JSON.stringify(result.admin));
    }
    if (result.access_token) {
      localStorage.setItem('admin_token', result.access_token);
    }
    return result;
  },
  
  updatePassword: (currentPassword, newPassword) => fetchAdminAPI('/admin/me/password', {
    method: 'PUT',
    body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
  }),
  
  getStats: () => fetchAdminAPI('/admin/stats'),
  
  isAuthenticated: () => !!getToken(),
  
  getUser: () => {
    const user = localStorage.getItem('admin_user');
    return user ? JSON.parse(user) : null;
  },
  
  isAdmin: () => {
    const user = authAPI.getUser();
    return user?.role === 'admin';
  },
  
  isEditor: () => {
    const user = authAPI.getUser();
    return user?.role === 'editor';
  },
};

// ============ User Management API (Admin Only) ============
export const usersAPI = {
  getAll: () => fetchAdminAPI('/admin/users'),
  getById: (id) => fetchAdminAPI(`/admin/users/${id}`),
  create: (data) => fetchAdminAPI('/admin/users', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => fetchAdminAPI(`/admin/users/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  resetPassword: (id, password) => fetchAdminAPI(`/admin/users/${id}/password?password=${encodeURIComponent(password)}`, {
    method: 'PUT',
  }),
  delete: (id) => fetchAdminAPI(`/admin/users/${id}`, {
    method: 'DELETE',
  }),
};

// ============ Admin Build Logs API ============
export const adminBuildLogsAPI = {
  getAll: () => fetchAdminAPI('/build-logs'),
  getById: (id) => fetchAdminAPI(`/build-logs/${id}`),
  create: (data) => fetchAdminAPI('/build-logs', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => fetchAdminAPI(`/build-logs/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => fetchAdminAPI(`/build-logs/${id}`, {
    method: 'DELETE',
  }),
  reorder: (id, direction) => fetchAdminAPI(`/build-logs/${id}/reorder?direction=${direction}`, {
    method: 'PUT',
  }),
};

// ============ Admin Team API ============
export const adminTeamAPI = {
  getAll: () => fetchAdminAPI('/team'),
  getById: (id) => fetchAdminAPI(`/team/${id}`),
  create: (data) => fetchAdminAPI('/team', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => fetchAdminAPI(`/team/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => fetchAdminAPI(`/team/${id}`, {
    method: 'DELETE',
  }),
  reorder: (id, direction) => fetchAdminAPI(`/team/${id}/reorder?direction=${direction}`, {
    method: 'PUT',
  }),
};

// ============ Admin Sponsors API ============
export const adminSponsorsAPI = {
  getAll: () => fetchAdminAPI('/sponsors'),
  getById: (id) => fetchAdminAPI(`/sponsors/${id}`),
  create: (data) => fetchAdminAPI('/sponsors', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
  update: (id, data) => fetchAdminAPI(`/sponsors/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  }),
  delete: (id) => fetchAdminAPI(`/sponsors/${id}`, {
    method: 'DELETE',
  }),
  reorder: (id, direction) => fetchAdminAPI(`/sponsors/${id}/reorder?direction=${direction}`, {
    method: 'PUT',
  }),
};

// ============ Admin Contact API ============
export const adminContactAPI = {
  getAll: () => fetchAdminAPI('/contact'),
  getById: (id) => fetchAdminAPI(`/contact/${id}`),
  markAsRead: (id) => fetchAdminAPI(`/contact/${id}/read`, {
    method: 'PUT',
  }),
  delete: (id) => fetchAdminAPI(`/contact/${id}`, {
    method: 'DELETE',
  }),
};

// ============ Admin Documents API ============
export const adminDocumentsAPI = {
  getAll: () => fetchAdminAPI('/documents'),
  getById: (id) => fetchAdminAPI(`/documents/${id}`),
  getDownloadUrl: (id) => `${API_BASE_URL}/api/documents/${id}/download`,
  upload: async (file, name, description = '', category = 'general') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', name);
    formData.append('description', description);
    formData.append('category', category);
    
    const token = getToken();
    const response = await fetch(`${API_BASE_URL}/api/documents`, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: formData,
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Upload failed');
    }
    
    return await response.json();
  },
  delete: (id) => fetchAdminAPI(`/documents/${id}`, {
    method: 'DELETE',
  }),
};

// ============ Backup / Restore API (Admin Only) ============
export const backupAPI = {
  export: () => fetchAdminAPI('/backup/export'),
  import: (data) => fetchAdminAPI('/backup/import', {
    method: 'POST',
    body: JSON.stringify(data),
  }),
};

export default {
  auth: authAPI,
  users: usersAPI,
  buildLogs: adminBuildLogsAPI,
  team: adminTeamAPI,
  sponsors: adminSponsorsAPI,
  contact: adminContactAPI,
  documents: adminDocumentsAPI,
  backup: backupAPI,
};
